// Copyright © 2024 Move Closer

import { Component } from 'vue-property-decorator'

import {
  ListTileConfig,
  ListType,
  TextListWithAddons,
  ThumbnailsListWithAddons,
  Tile,
  TileConfig,
  TileSize,
  TileSizeMobile
} from '../_components/ui'
import { AnyModule, ContentSneakPeakData, ContentType, ModuleAdditionalRows } from '../_contracts'
import { TeaserBoxInnerGrid, TeaserBoxVersion } from './TeaserBox.contracts'
import { addonsRegistry } from './TeaserBox.config'
import { InnerGridWrapper } from './_partials'

/**
 * Shared interfaces for TeaserBox components with addon support
 */
export interface MergedItem {
  type: ContentType.Article | 'addon' | 'component';
  tileType: TileSize.L | TileSize.S | TileSize.XL | ListType.LIST | 'addon';
  component: string;
  data: ContentSneakPeakData | AnyModule | MergedItem[];
  props: Record<string, unknown>;
  position: number;
  key: string;
}

export interface AddonWithPosition {
  module: AnyModule;
  position?: number | string;
}

export interface GridConfigType {
  mobileSize: TileSizeMobile;
  defaultSize: TileSize;
  tiles: Record<number, TileSize>;
  listOffset: number;
  listType: ListType | boolean;
}

/**
 * Base mixin for TeaserBox components with addon support
 * Provides common functionality for handling addons and merged item lists
 */
@Component({
  name: 'TeaserBoxAddonsMixin',
  components: { InnerGridWrapper, TextListWithAddons, Tile, ThumbnailsListWithAddons },
  template: `
    <InnerGridWrapper>
    <template v-if="items && items.length">
      <!-- Left column (3/4): Tiles and Addons -->
      <template v-for="item in tiles">
        <component :is="getComponentType(item)" v-bind="getComponentProps(item)" :key="item.key" />
      </template>
      <!-- Right column (1/4): TextList with Addons -->
      <template v-if="!isMobile && listComponent">
        <div :class="gridStyle">
          <component :is="listComponent.component" v-bind="getComponentProps(listComponent)"
                     :key="listComponent.key" />
        </div>
      </template>
    </template>
    </InnerGridWrapper>
  `
})
export class TeaserBoxAddonsMixin extends TeaserBoxInnerGrid {
  public listComponent: MergedItem | null = null
  public tiles: MergedItem[] = []
  public gridConfig: GridConfigType = {
    defaultSize: TileSize.S,
    mobileSize: TileSizeMobile.TILE_PLUS_TEXT,
    tiles: {
      0: TileSize.L
    },
    listOffset: 5,
    listType: ListType.LIST
  }

  public readonly TileSize = TileSize

  get moduleVersion (): TeaserBoxVersion {
    return (this.constructor as typeof TeaserBoxAddonsMixin).moduleVersion
  }

  public created () {
    this.tiles = this.getTilesAndAddons()
    if (this.gridConfig.listType !== false) {
      this.listComponent = this.combineToListComponent()
    }
  }

  public get tilesOffset (): number {
    return this.gridConfig.listOffset
  }

  /**
   * Common grid style getter for text list layouts
   */
  public get gridStyle (): string {
    switch (this.teaserBoxConfig.additionalRows) {
      case ModuleAdditionalRows.None:
        return 'teaser-box__list-wrapper'
      case ModuleAdditionalRows.addOne:
        return 'teaser-box__list-wrapper-three-rows'
      case ModuleAdditionalRows.addTwo:
        return 'teaser-box__list-wrapper-four-rows'
      default:
        return 'teaser-box__list-wrapper'
    }
  }

  /**
   * Gets component type for merged items - shared logic across all components
   */
  public getComponentType (item: MergedItem): string {
    switch (item.tileType) {
      case TileSize.L:
      case TileSize.S:
      case TileSize.XL:
        return 'Tile'
      case 'addon':
        return (addonsRegistry as Record<string, string>)[(item.data as AnyModule).driver] || 'div'
      default:
        return 'div'
    }
  }

  public getListComponent (type: string): string {
    switch (type) {
      case 'thumbnails':
        return 'ThumbnailsListWithAddons'
      case 'list':
      default:
        return 'TextListWithAddons'
    }
  }

  /**
   * Gets component props for merged items - shared logic with extension points
   */
  public getComponentProps (item: MergedItem): object {
    switch (item.tileType) {
      case TileSize.S:
        return this.getTileSmallProps(item)
      case TileSize.L:
        return this.getTileLargeProps(item)
      case TileSize.XL:
        return this.getTileXLargeProps(item)
      case ListType.LIST:
        return this.getListsProps(item)
      case 'addon':
        return {
          module: item.data,
          ...this.getAddonProps(item)
        }
      default:
        return {}
    }
  }

  /**
   * Extension point for large tile props
   */
  public getTileLargeProps (item: MergedItem): object {
    return {
      ...this.tileConfig[TileSize.L],
      article: item.data,
      fallbackImage: this.fallbackImage,
      windowService: this.windowService,
      isLazy: this.isLazy,
      isMobile: this.isMobile
    }
  }

  /**
   * Extension point for small tile props
   */
  public getTileSmallProps (item: MergedItem): object {
    return {
      ...this.tileConfig[TileSize.S],
      article: item.data,
      fallbackImage: this.fallbackImage,
      windowService: this.windowService,
      isLazy: this.isLazy,
      isMobile: this.isMobile,
      titleOnImage: true
    }
  }

  /**
   * Extension point for extra large tile props
   */
  public getTileXLargeProps (item: MergedItem): object {
    return {
      ...this.tileConfig[TileSize.XL],
      article: item.data,
      fallbackImage: this.fallbackImage,
      windowService: this.windowService,
      isLazy: this.isLazy,
      isMobile: this.isMobile
    }
  }

  /**
   * Extension point for thumbnails list with addons props
   */
  public getListsProps (item: MergedItem): object {
    return {
      ...this.listConfig[this.gridConfig.listType as ListType],
      items: item.data,
      fallbackImage: this.fallbackImage,
      windowService: this.windowService,
      listConfig: this.teaserBoxConfig,
      readMoreLink: this.readMoreLink,
      isLazy: this.isLazy,
      addonProps: this.getAddonProps(item)
    }
  }

  /**
   * Extension point for addon props (TeaserBox version with services)
   */
  public getAddonProps (item: MergedItem): object {
    // Handle case where addon might be an array instead of single module
    const module = Array.isArray(item.data) ? item.data[0] : item.data
    return {
      module,
      eventBus: this.eventBus,
      relatedService: this.relatedService,
      shareService: this.shareService,
      windowService: this.windowService,
      theme: this.theme,
      fallbackImage: this.fallbackImage,
      isMobile: this.isMobile,
      onEmptyCallback: this.onEmptyCallback,
      pdfService: null,
      forceHide: false
    }
  }

  /**
   * Gets tiles and addons for the first part of the layout
   */
  public getTilesAndAddons (): MergedItem[] {
    return this.items.slice(0, this.tilesOffset).map(item => {
      if (item.type === ContentType.Article && this.gridConfig.tiles[item.position]) {
        return { ...item, tileType: this.gridConfig.tiles[item.position] }
      }
      return { ...item }
    })
  }

  /**
   * Gets text list and addons for the second part of the layout
   */
  public combineToListComponent (): MergedItem {
    const items = this.items.slice(this.tilesOffset)
    const listType = this.gridConfig.listType !== false ? this.gridConfig.listType as ListType : ListType.LIST
    return {
      type: 'component',
      tileType: ListType.LIST,
      component: this.getListComponent(listType),
      data: items,
      props: this.$props,
      position: this.tilesOffset,
      key: 'list'
    }
  }

  private get listConfig (): Record<ListType, ListTileConfig> {
    return {
      [ListType.THUMBNAILS]: {
        showArticleTags: this.teaserBoxConfig.showArticleTags,
        titleVersion: this.teaserBoxConfig.titleVersion,
        tile: {
          isPremium: this.teaserBoxConfig.isPremium,
          showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
          showAuthorName: this.teaserBoxConfig.showAuthorName,
          showCategory: this.teaserBoxConfig.showCategory,
          showPublicationDate: this.teaserBoxConfig.showPublicationDate,
          showTitle: true,
          size: TileSize.S,
          mobileSize: this.gridConfig.mobileSize,
          titleOnImage: true,
          gradientColor: this.teaserBoxConfig.gradientColor,
          showArticleTags: this.teaserBoxConfig.showArticleTags
        }
      },
      [ListType.LIST]: {
        showArticleTags: this.teaserBoxConfig.showArticleTags,
        titleVersion: this.teaserBoxConfig.titleVersion,
        tile: {
          isPremium: this.teaserBoxConfig.isPremium,
          showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
          showAuthorName: this.teaserBoxConfig.showAuthorName,
          showCategory: this.teaserBoxConfig.showCategory,
          showPublicationDate: this.teaserBoxConfig.showPublicationDate,
          showTitle: true,
          size: TileSize.S,
          mobileSize: this.gridConfig.mobileSize,
          titleOnImage: true,
          gradientColor: this.teaserBoxConfig.gradientColor,
          showArticleTags: this.teaserBoxConfig.showArticleTags
        }
      }
    }
  }

  private get tileConfig (): Record<TileSize, TileConfig> {
    return {
      [TileSize.S]: {
        isPremium: this.teaserBoxConfig.isPremium,
        showArticleTags: this.teaserBoxConfig.showArticleTags,
        showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
        showAuthorName: this.teaserBoxConfig.showAuthorName,
        showCategory: false,
        showPublicationDate: this.teaserBoxConfig.showPublicationDate,
        showTitle: true,
        size: TileSize.S,
        mobileSize: this.gridConfig.mobileSize,
        titleOnImage: true,
        titleVersion: this.teaserBoxConfig.titleVersion,
        gradientColor: this.teaserBoxConfig.gradientColor
      },
      [TileSize.L]: {
        isPremium: this.teaserBoxConfig.isPremium,
        showArticleTags: this.teaserBoxConfig.showArticleTags,
        showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
        showAuthorName: this.teaserBoxConfig.showAuthorName,
        showCategory: this.teaserBoxConfig.showCategory,
        showPublicationDate: this.teaserBoxConfig.showPublicationDate,
        showTitle: true,
        size: TileSize.L,
        mobileSize: TileSizeMobile.TILE,
        titleOnImage: true,
        titleVersion: this.teaserBoxConfig.titleVersion,
        gradientColor: this.teaserBoxConfig.gradientColor
      },
      [TileSize.XL]: {
        isPremium: this.teaserBoxConfig.isPremium,
        showArticleTags: this.teaserBoxConfig.showArticleTags,
        showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
        showAuthorName: this.teaserBoxConfig.showAuthorName,
        showCategory: this.teaserBoxConfig.showCategory,
        showPublicationDate: this.teaserBoxConfig.showPublicationDate,
        showTitle: true,
        size: TileSize.XL,
        mobileSize: TileSizeMobile.TILE,
        titleOnImage: true,
        titleVersion: this.teaserBoxConfig.titleVersion,
        gradientColor: this.teaserBoxConfig.gradientColor
      }
    }
  }
}
